import { useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import { Message, ChatFolderDetail } from "../interfaces/chat.interface";
import { ConversationType, CHAT_CONFIG } from "../constants/chat.constant";
import useChatApiService from "../services/chat.service";
import useChatLocalService from "../services/chat-local.service";
import { ChatInitialState } from "../interfaces/chat.interface";
import { useTokenUsageApi } from "@/services/token.service";
import { CreditsContext } from "@/store/credits.store";

/**
 * Chat actions interface containing all chat action handlers
 */
export interface ChatActions {
  handleSend: (message: Message) => Promise<void>;
  handleConversationTypeChange: (newType: ConversationType) => void;
  handleTemperatureChange: (temperature: number) => void;
  handleReasoningEffortChange: (effort: number) => void;
  handleModelChange: (model: string) => void;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  createNewConversation: () => ChatFolderDetail;
  showErrorMessage: () => void;
  streamAIResponse: (
    conversation: ChatFolderDetail,
    message: Message,
    assistantIndex: number
  ) => Promise<void>;
  cancelStream: () => void;
}

/**
 * useChatActions hook provides all chat action handlers including message sending,
 * streaming, model/temperature/effort changes, conversation management, and error handling.
 * Manages Web Worker communication for streaming responses and handles conversation
 * creation, cancellation, and state synchronization.
 * 
 * @hook
 * @param {any} chatState - Chat state from useChatState hook
 * @param {ChatInitialState} state - Global chat state from context
 * @param {Function} dispatch - Context dispatch function
 * @param {ConversationType} conversationType - Current conversation type
 * @param {(type: ConversationType) => void} setConversationType - Function to change conversation type
 * @param {() => void} refetchFolders - Function to refetch conversation folders
 * @param {() => void} [closeSources] - Optional function to close sources modal
 * @param {boolean} [isSensitive] - Optional sensitive chat mode flag
 * @returns {ChatActions} Object containing all chat action handlers
 * 
 * @example
 * ```tsx
 * const chatActions = useChatActions(
 *   chatState,
 *   state,
 *   dispatch,
 *   conversationType,
 *   setConversationType,
 *   refetchFolders
 * );
 * 
 * // Send a message
 * await chatActions.handleSend({
 *   role: 'user',
 *   content: 'Hello'
 * });
 * 
 * // Change model
 * chatActions.handleModelChange('gpt-4');
 * ```
 */
export const useChatActions = (
  chatState: any,
  state: ChatInitialState,
  dispatch: any,
  conversationType: ConversationType,
  setConversationType: (type: ConversationType) => void,
  refetchFolders: () => void,
  closeSources?: () => void,
  isSensitive?: boolean
): ChatActions => {
  const navigate = useNavigate();
  const { getChopGptResponse, getStreamWorker } = useChatApiService();
  const { saveSelectedConversation } = useChatLocalService();
  const { getTokenCredits } = useTokenUsageApi();
  const { state : creditState, dispatch: creditDispatch } = useContext(CreditsContext);
  const workerRef = useRef<Worker | null>(null);



  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      chatState.setContent(value);
      chatState.updateContentForConversation(
        state.selectedConversation?.session_id || "new",
        value
      );
    },
    [chatState, state.selectedConversation?.session_id]
  );

  const handleConversationTypeChange = useCallback(
    (newType: ConversationType) => {
      chatState.setActiveConversation([]);
      setConversationType(newType);
      chatState.setStreamingConversation(null);
      chatState.setStreamingConversationId(null);
      chatState.setContent("");

      dispatch({ field: "conversations", value: [] });
      dispatch({ field: "conversationStatusMap", value: new Map() });

      refetchFolders();
    },
    [chatState, setConversationType, dispatch, refetchFolders]
  );

  const handleTemperatureChange = useCallback(
    (temperature: number) => {
      chatState.setModelTemperature(temperature.toString());
    },
    [chatState]
  );

  const handleReasoningEffortChange = useCallback(
    (effort: number) => {
      chatState.setModelEffort(effort.toString());
    },
    [chatState]
  );

  const handleModelChange = useCallback(
    (model: string) => {
      chatState.setSelectedModel(model);
      if (model === "gpt-5.6-terra" || model === "gpt-5.2" || model ==="gpt-5") {
        chatState.setModelTemperature("1");
      } else {
        chatState.setModelTemperature(String(CHAT_CONFIG.TEMPERATURE.DEFAULT));
        if (CHAT_CONFIG.REASONING_EFFORT.LOW_LIMIT_REASONING_MODELS.includes(model))
          chatState.setModelEffort('2');
        else
          chatState.setModelEffort(String(CHAT_CONFIG.REASONING_EFFORT.DEFAULT));
      }
    },
    [chatState]
  );

  const cancelStream = () => {
    workerRef.current?.postMessage({ type: "cancel" });
  };

  const createNewConversation = useCallback((): ChatFolderDetail => {
    const conversation = {
      session_id: uuidv4(),
      title: "New Conversation",
      conversation_type: conversationType,
    };

    dispatch({ field: "selectedConversation", value: conversation });
    dispatch({
      field: "conversations",
      value: [conversation, ...state.conversations],
    });

    const statusMap = new Map(state.conversationStatusMap);
    statusMap.set(conversation.session_id, false);
    dispatch({ field: "conversationStatusMap", value: statusMap });

    // Set streaming conversation ID
    chatState.setStreamingConversationId(conversation.session_id);

    const urlType = conversationType.toLowerCase().replace(/_/g, "-");
    navigate(`/chat/${urlType}/${conversation.session_id}`, {
      replace: true,
    });

    return conversation;
  }, [
    conversationType,
    dispatch,
    state.conversations,
    state.conversationStatusMap,
    navigate,
    chatState,
  ]);

  const showErrorMessage = useCallback((): void => {
    const updateMessages = (prev: Message[] | null): Message[] => {
      const messages = [...(prev || [])];

      if (
        messages[messages.length - 1]?.role === "assistant" &&
        !messages[messages.length - 1].content
      ) {
        messages.pop();
      }

      messages.push({
        role: "assistant",
        content: "Sorry, an error occurred. Please try again.",
      });

      return messages;
    };

    if (chatState.streamingConversation) {
      chatState.setStreamingConversation(
        updateMessages(chatState.streamingConversation)
      );
    }
  }, [chatState]);

  const streamAIResponse = useCallback(
    async (
      conversation: ChatFolderDetail,
      message: Message,
      assistantIndex: number
    ): Promise<void> => {
      return new Promise(async (resolve, reject) => {
        try {
          if (!workerRef.current) {
            workerRef.current = getStreamWorker();
          }
          const worker = workerRef.current;

          await getChopGptResponse(
            {
              conversationId: conversation.session_id,
              conversationType: conversation.conversation_type,
              model: chatState.selectedModel,
              temperature: chatState.modelTemperature,
              effort: chatState.modelEffort,
              message: message.content,
              files: message.selectedFiles,
              sensitive: isSensitive || false,
            },
            worker
          );

          worker.onmessage = async (event) => {
            if (event.data.type === "toast") {
              const { message, severity } = event.data;

              if (severity === "error") {
                toast.error(message, {
                  duration: 5000,
                  position: "top-right",
                });
              }
            }

            if (event.data.type === "error") {
              toast.error(event.data.error || "An error occurred", {
                duration: 5000,
                position: "top-right",
              });

              chatState.setStreamingConversation((prev: any) => {
                const updated = [...(prev || [])];
                updated[assistantIndex] = {
                  role: "assistant",
                  content: `Error: ${
                    event.data.error ||
                    "An unexpected error occurred. Please try again."
                  }`,
                };
                return updated;
              });

              worker.terminate();
              workerRef.current = null;
              reject(new Error(event.data.error));
            }

            if (event.data.type === "update") {
              chatState.setIsLoading(false);

              const { id, sessionId, content, reasoning, hasError, citations } =
                event.data.data;
              
              const mappedCitations =
                citations?.map((citation: any) => ({
                  id: citation.citation_number || citation.id || 0,
                  message_id: id || 0,
                  document_id: citation.document_id || null,
                  chunk_id: citation.chunk_id || "",
                  source_file:
                    citation.filename ||
                    citation.source_file ||
                    citation.url ||
                    "",
                  relevance_score: citation.relevance_score || 0,
                  content_snippet:
                    citation.content_preview || citation.content_snippet || "",
                  created_at: new Date().toISOString(),
                  url: citation.url || citation.filename || "",
                })) || [];

              chatState.setStreamingConversation((prev: any) => {
                const updated = [...(prev || [])];
                const isStreamingComplete = updated[assistantIndex] ? updated[assistantIndex].isStreamingComplete : false
                updated[assistantIndex] = {
                  id,
                  sessionId,
                  role: "assistant",
                  content,
                  reasoning,
                  hasError,
                  isStreamingComplete: isStreamingComplete,
                  citations:
                    mappedCitations.length > 0
                      ? mappedCitations
                      : updated[assistantIndex]?.citations || [],
                  conversationType: conversationType,
                };
                return updated;
              });
            } else if (event.data.type === "done") {
              const {
                id,
                sessionId,
                displayedContent,
                displayedReasoning,
                hasError,
                errorMessage,
                citations,
              } = event.data.data;

              const mappedCitations =
                citations?.map((citation: any) => ({
                  id: citation.citation_number || citation.id || 0,
                  message_id: id || 0,
                  document_id: citation.document_id || null,
                  chunk_id: citation.chunk_id || "",
                  source_file:
                    citation.filename ||
                    citation.source_file ||
                    citation.url ||
                    "",
                    file_path: citation.file_path || citation.blob_url || citation.url || "",
                  relevance_score: citation.relevance_score || 0,
                  content_snippet:
                    citation.content_preview || citation.content_snippet || "",
                  created_at: new Date().toISOString(),
                  url: citation.url || citation.filename || "",
                })) || [];

              chatState.setStreamingConversation((prev: Message[] | null) => {
                const updated = [...(prev || [])];
                const isStreamCompleted = updated[assistantIndex] ? updated[assistantIndex].isStreamingComplete : false
                updated[assistantIndex] = {
                  id: id,
                  sessionId: sessionId,
                  role: "assistant",
                  content: displayedContent,
                  hasError: hasError,
                  reasoning: displayedReasoning,
                  isStreamingComplete: isStreamCompleted,
                  citations:
                    mappedCitations.length > 0
                      ? mappedCitations
                      : updated[assistantIndex]?.citations || [],
                  conversationType: conversationType,
                };
                return updated;
              });

              try {
                getTokenCredits().then((response) => {
                  creditDispatch({
                    field: "model_groups",
                    value: response.data,
                  });
                });
              } catch (err) {
                console.error("Failed to refresh credits:", err);
              }

              worker.terminate();
              workerRef.current = null;
              resolve();
            } else if (event.data.type === "cancel") {

              const message = "Messages cancelled mid-stream are not saved."
              toast(message, {
                duration: 6000,
                position: "top-right",
              });


              chatState.setStreamingConversation((prev: Message[]) => {
                const updated = [...(prev || [])];

                const lastAiMessage = updated[assistantIndex]
                const lastpromptMessage = updated[assistantIndex - 1]

                lastAiMessage.isCancelled = true
                lastpromptMessage.isCancelled = true

                return updated
              })

              worker.terminate();
              workerRef.current = null;
              resolve();
            } else if (event.data.type === "content_end") {
              chatState.setStreamingConversation((prev: Message[]) => {
                const updated = [...(prev || [])];
                const lastAiMessage = updated[assistantIndex]
                lastAiMessage.isStreamingComplete = true;
                return updated
              })
            }
          };

          worker.onerror = (error) => {
            console.error("Worker error:", error);
            worker.terminate();
            workerRef.current = null;
            reject(error);
          };
        } catch (error) {
          console.error("Stream error:", error);
          reject(error);
        }
      });
    },
    [getChopGptResponse, chatState, isSensitive, getStreamWorker]
  );

  const removeCancelledPair = (messages: Message[]): Message[] => {
    if (!messages || messages.length < 3) return messages;
  
    const last = messages[messages.length - 2];
    const secondLast = messages[messages.length - 3];
  
    if (last?.isCancelled && secondLast?.isCancelled) {
      return [
        ...messages.slice(0, messages.length - 3),
        messages[messages.length - 1],
      ];
    }
  
    return messages;
  };

  
  const handleSend = useCallback(
    async (message: Message) => {
      const content = message.content?.trim();
      if (!content) return;

      message.content = content;
      chatState.setIsResponseGenerating(true);

      // Close sources modal when user sends a message
      if (closeSources) closeSources();

      let currentConversation = state.selectedConversation;

      if (!currentConversation) {
        const newConversation = {
          session_id: uuidv4(),
          title: "New Conversation",
          conversation_type: conversationType,
          sensitive: isSensitive || false,
        };

        currentConversation = newConversation;

        const updatedConversations = [newConversation, ...state.conversations];

        const newStatusMap = new Map(state.conversationStatusMap);
        newStatusMap.set(newConversation.session_id, false);

        dispatch({ field: "selectedConversation", value: newConversation });
        dispatch({ field: "conversationStatusMap", value: newStatusMap });
        dispatch({ field: "conversations", value: updatedConversations });

        const urlType = newConversation.conversation_type
          .toLowerCase()
          .replace(/_/g, "-");
        navigate(`/chat/${urlType}/${newConversation.session_id}`, {
          replace: false,
        });

        // Save the selected conversation to local storage
        saveSelectedConversation(newConversation.session_id, conversationType);
      } else if (currentConversation && isSensitive !== undefined) {
        if (currentConversation.sensitive !== isSensitive) {
          const updatedConversation = {
            ...currentConversation,
            sensitive: isSensitive,
          };
          dispatch({
            field: "selectedConversation",
            value: updatedConversation,
          });
          currentConversation = updatedConversation;
        }
      }

      chatState.setCurrentMessage(message);

      let initialMessages = [...(chatState.activeConversation || [])];
      initialMessages = [...initialMessages, message];

      initialMessages = removeCancelledPair(initialMessages)

      chatState.setActiveConversation(initialMessages);

      // Set streaming conversation ID and initial state
      chatState.setStreamingConversationId(currentConversation.session_id);
      chatState.setStreamingConversation(initialMessages);

      // Clear input immediately after sending message
      chatState.setContent("");
      chatState.clearContentForConversation(
        state.selectedConversation?.session_id || "new"
      );

      chatState.setIsLoading(true);

      try {
        let conversation = state.selectedConversation
          ? state.selectedConversation
          : currentConversation;
        if (!conversation) {
          conversation = createNewConversation();
        }

        const assistantIndex = initialMessages.length;
        const messagesWithAssistant: Message[] = [
          ...initialMessages,
          {
            role: "assistant" as const,
            content: "",
            reasoning: "",
          },
        ];

        chatState.setStreamingConversation(messagesWithAssistant);

        await streamAIResponse(conversation, message, assistantIndex);
        refetchFolders();
      } catch (error) {
        console.error("Send message error:", error);
        showErrorMessage();
        chatState.setStreamingConversation(null);
        chatState.setStreamingConversationId(null);

        // Refresh sidebar even on error to ensure consistency
        refetchFolders();
      } finally {
        chatState.setIsResponseGenerating(false);
        chatState.setIsLoading(false);
        chatState.setContent("");
        chatState.clearContentForConversation(
          state.selectedConversation?.session_id || "new"
        );

        // Sync streaming conversation to active conversation when streaming completes
        if (
          chatState.streamingConversation &&
          chatState.streamingConversationId
        ) {
          chatState.setActiveConversation(chatState.streamingConversation);
          chatState.setStreamingConversation(null);
          chatState.setStreamingConversationId(null);

          // Update conversation title after streaming completes
          const conversation = state.selectedConversation;

          // Refresh sidebar after conversation is fully processed
          refetchFolders();
        }
      }
    },
    [
      chatState,
      state.selectedConversation,
      state.conversations,
      state.conversationStatusMap,
      conversationType,
      dispatch,
      navigate,
      createNewConversation,
      streamAIResponse,
      showErrorMessage,
      refetchFolders,
      closeSources,
    ]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const isMobile = () => {
        const userAgent =
          typeof window.navigator === "undefined" ? "" : navigator.userAgent;
        const mobileRegex =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
        return mobileRegex.test(userAgent);
      };

      if (
        e.key === "Enter" &&
        !chatState.isResponseGenerating &&
        !chatState.isTyping &&
        !isMobile() &&
        !e.shiftKey
      ) {
        e.preventDefault();
        chatState.setIsResponseGenerating(true);
        handleSend({
          role: "user",
          content: chatState.content as string,
        });
      }
    },
    [chatState, handleSend]
  );

  return {
    handleSend,
    handleConversationTypeChange,
    handleTemperatureChange,
    handleReasoningEffortChange,
    handleModelChange,
    handleChange,
    handleKeyDown,
    createNewConversation,
    showErrorMessage,
    streamAIResponse,
    cancelStream
  };
};
